import { useRequiredContext } from "@redotech/react-util/context";
import { useRpcData } from "@redotech/react-util/rpc-data";
import { returnsCenterIdFormat } from "@redotech/redo-model/ids";
import {
  DEFAULT_RETURNS_PROCESSING_COST,
  DEFAULT_RETURNS_PROCESSING_FEE,
  Team,
} from "@redotech/redo-model/team";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import {
  groupInput,
  input,
  InputProvider,
  numberValidator,
} from "@redotech/ui/form";
import { memo, useCallback, useMemo } from "react";
import { RedoAdminRpcClientContext } from "../../../../app/redo-admin-rpc-client-provider";

export const redoManagedReturnsSettingsForm = groupInput({
  enabled: input<boolean>(),
  processingFee: input<string>({ validator: numberValidator({ min: 0 }) }),
  processingCost: input<string>({ validator: numberValidator({ min: 0 }) }),
  coverageShare: input<string>({ validator: numberValidator({ min: 0 }) }),
  defaultReturnsCenterId: input<string | null>(),
});

export const redoManagedReturnsSettingsDefault = {
  enabled: false,
  processingFee: `${DEFAULT_RETURNS_PROCESSING_FEE}`,
  processingCost: `${DEFAULT_RETURNS_PROCESSING_COST}`,
  coverageShare: "",
  defaultReturnsCenterId: null,
};

export type RedoManagedReturnsSettingsForm = InputProvider.Form<
  typeof redoManagedReturnsSettingsForm
>;
export type RedoManagedReturnsSettingsValue = InputProvider.Value<
  typeof redoManagedReturnsSettingsForm
>;

export const redoManagedReturnsTeamToFormValue = (
  team: Team,
): RedoManagedReturnsSettingsValue => {
  const storedId = team.settings.redoManagedReturns?.defaultReturnsCenterId;
  const formattedReturnsCenterId =
    storedId && !returnsCenterIdFormat.test(storedId)
      ? returnsCenterIdFormat.write(BigInt(storedId))
      : (storedId ?? null);

  return {
    enabled: team.settings.redoManagedReturns?.enabled ?? false,
    processingFee: (
      team.settings.redoManagedReturns?.processingFee ??
      DEFAULT_RETURNS_PROCESSING_FEE
    ).toString(),
    processingCost: (
      team.settings.redoManagedReturns?.processingCost ??
      DEFAULT_RETURNS_PROCESSING_COST
    ).toString(),
    coverageShare: team.redoManagedReturnsPricePerOrder ?? "",
    defaultReturnsCenterId: formattedReturnsCenterId,
  };
};

export const redoManagedReturnsFormValueToTeam = (
  redoManagedReturnsSettings: RedoManagedReturnsSettingsValue,
): Team["settings"]["redoManagedReturns"] => {
  const formattedId = redoManagedReturnsSettings.defaultReturnsCenterId;
  const rawReturnsCenterId =
    formattedId && returnsCenterIdFormat.test(formattedId)
      ? returnsCenterIdFormat.read(formattedId).toString()
      : (formattedId ?? undefined);

  return {
    enabled: redoManagedReturnsSettings.enabled,
    processingFee: isNaN(Number(redoManagedReturnsSettings.processingFee))
      ? DEFAULT_RETURNS_PROCESSING_FEE
      : Number(redoManagedReturnsSettings.processingFee),
    processingCost: isNaN(Number(redoManagedReturnsSettings.processingCost))
      ? DEFAULT_RETURNS_PROCESSING_COST
      : Number(redoManagedReturnsSettings.processingCost),
    defaultReturnsCenterId: rawReturnsCenterId,
  };
};

interface ReturnsCenterOption {
  id: string;
  name: string;
  city: string | null;
  state: string | null;
}

export const RedoManagedReturnsSettingsCardV2 = memo(
  function RedoManagedReturnsSettingsCardV2({
    input,
  }: {
    input: RedoManagedReturnsSettingsForm;
  }) {
    const rpcClient = useRequiredContext(RedoAdminRpcClientContext);

    const {
      value: returnsCentersData,
      pending: returnsCentersPending,
      error: returnsCentersError,
    } = useRpcData(rpcClient, "getReturnsCenters", { input: { sort: null } });
    const returnsCenters = useMemo(
      () => returnsCentersData?.returnsCenters ?? [],
      [returnsCentersData],
    );

    const dropdownOptions: RedoListItem<ReturnsCenterOption>[] = useMemo(() => {
      return returnsCenters.map((center) => ({
        type: "text" as const,
        id: center.id,
        value: {
          id: center.id,
          name: center.name,
          city: center.city,
          state: center.state,
        },
        text: `${center.name} - ${center.city}, ${center.state}`,
      }));
    }, [returnsCenters]);

    const selectedOption = useMemo(() => {
      const selectedId = input.inputs.defaultReturnsCenterId.value;
      if (!selectedId) return undefined;
      return dropdownOptions.find((option) => option.id === selectedId);
    }, [dropdownOptions, input.inputs.defaultReturnsCenterId.value]);

    const handleCenterSelected = useCallback(
      ({ item }: { item: RedoListItem<ReturnsCenterOption> }) => {
        input.inputs.defaultReturnsCenterId.setValue(item.value.id);
      },
      [input.inputs.defaultReturnsCenterId],
    );

    return (
      <>
        <RedoTextInput
          IconTrailing={() => <span>USD</span>}
          label="Processing Fee (per return)"
          min={0}
          placeholder="0.00"
          setValue={input.inputs.processingFee.setValue}
          state={!input.inputs.enabled.value ? "disabled" : "default"}
          step={0.01}
          value={input.inputs.processingFee.value}
        />

        <RedoTextInput
          IconTrailing={() => <span>USD</span>}
          label="3PL Processing Cost (per return)"
          min={0}
          placeholder="0.00"
          setValue={input.inputs.processingCost.setValue}
          state={!input.inputs.enabled.value ? "disabled" : "default"}
          step={0.01}
          value={input.inputs.processingCost.value}
        />

        <RedoTextInput
          description="Amount of coverage price that will be invoiced as Redo Managed Returns coverage (subtracts from the coverage price)"
          IconTrailing={() => <span>USD</span>}
          label="RMR Coverage Share"
          min={0}
          placeholder="0.00 (no rev share)"
          setValue={input.inputs.coverageShare.setValue}
          state={!input.inputs.enabled.value ? "disabled" : "default"}
          step={0.01}
          value={input.inputs.coverageShare.value}
        />

        <Flex dir="column" gap="sm">
          <Text fontSize="sm" fontWeight="semibold">
            Set Returns Shipping Address
          </Text>
          <RedoSingleSelectDropdownInput
            disabled={returnsCentersPending}
            emptyListMessage="No returns centers configured"
            itemsLoading={returnsCentersPending}
            label="Default Returns Center"
            options={dropdownOptions}
            optionSelected={handleCenterSelected}
            placeholder="Select a returns center..."
            selectedItem={selectedOption}
            size="md"
          />
          {!!returnsCentersError && (
            <Text fontSize="sm" textColor="error">
              {returnsCentersError instanceof Error
                ? returnsCentersError.message
                : "An error occurred while loading returns centers"}
            </Text>
          )}
        </Flex>
      </>
    );
  },
);
